import { isMobile } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';

import { FeaturedProductsCompatibleBlock } from '../../Blocks/Products/FeaturedProducts/FeaturedProductsCompatibleBlock/FeaturedProductsCompatibleBlock';
import { BlockSectionClassResolver } from '../../BlockSection/BlockSectionClassResolver';
import { UserContextProvider } from '../../ReactComponents/Contexts/UserContext';
import TouchSwiper from '../../ReactComponents/TouchSwiper/TouchSwiper';
import { ProductCard } from '../ProductCard/ProductCard';
import { ProductCardPlaceholder } from '../ProductCard/ProductCardPlaceholder/ProductCardPlaceholder';
import { ProductCardVariant } from '../ProductCard/ProductCardVariant.csharp';
import { ProductListProps } from './ProductListProps.csharp';
import { ProductListUtils } from './ProductListUtils';
import { ProductListVariant } from './ProductListVariant.csharp';
import { ProductListViewModel } from './ProductListViewModel.csharp';

interface IProductListProps extends ProductListProps {
    isLoading?: boolean;
    withPlaceholders?: boolean;
    skipProductsAmountOnMobile?: number;
    skipProductsAmountOnTablet?: number;
    skipProductsAmountOnDesktop?: number;
    disableProductQuantityPicker?: boolean;
}

export const ProductList = (props: IProductListProps) => {
    const { profile, cartItemUrl, validateItemUrl, rxUrl, epiFindClickTrackingUrl, experimentProductClickUrl, model, disableProductQuantityPicker } = props;
    const { heading, products } = props.model as ProductListViewModel;

    const variant = props.variant != null ? props.variant : ProductListVariant.GridWithSwiper;
    const productListUtils = new ProductListUtils(props.model);
    const hideReviewSnippets = products.every((p) => p.hidePowerReviews || !p.powerReviewsRating);

    const getProductCardVariant = (): ProductCardVariant => {
        if (variant === ProductListVariant.Horizontal) {
            return ProductCardVariant.Horizontal;
        } else if (variant === ProductListVariant.HorizontalSimple) {
            return ProductCardVariant.HorizontalSimple;
        } else if (variant === ProductListVariant.CartHorizontal) {
            return ProductCardVariant.CartHorizontal;
        } else {
            return ProductCardVariant.Vertical;
        }
    };

    const getVariantModifier = (): string => {
        if ((variant === ProductListVariant.GridWithSwiper || variant === ProductListVariant.CartHorizontal) && !shouldRenderSwiper()) {
            return `ProductList--${ProductListVariant[ProductListVariant.GridWithoutSwiper]}`;
        }

        return `ProductList--${ProductListVariant[props.variant]}`;
    };

    const shouldRenderSwiper = (): boolean => {
        const minProductsAmount = 3;
        const { maxProductsAmountOnMobile, products } = props.model;

        const variantCondition = variant === ProductListVariant.GridWithSwiper || (isMobile() && variant === ProductListVariant.CartHorizontal);
        const productAmountCondition =
            (products.length >= minProductsAmount || !!props.isLoading) && (maxProductsAmountOnMobile === 0 || maxProductsAmountOnMobile >= minProductsAmount);

        return variantCondition && productAmountCondition;
    };

    const renderProductsWithAdditionalBlocks = () => {
        return props.variant === ProductListVariant.Horizontal ||
            props.variant === ProductListVariant.CartHorizontal ||
            props.variant === ProductListVariant.HorizontalSimple ? (
            renderProducts()
        ) : (
            <>
                {props.model.leadingContent && <FeaturedProductsCompatibleBlock model={props.model.leadingContent} />}
                {renderProducts()}
                {props.model.moreInfoContent && <FeaturedProductsCompatibleBlock model={props.model.moreInfoContent} />}
            </>
        );
    };

    const renderProducts = () => {
        return props.isLoading ? renderPlaceholders() : renderProductCards();
    };

    const renderProductCards = () => {
        const [maxOnMobile, maxOnTablet, maxOnDesktop] = productListUtils.GetVisibleProductsCount();

        return products.map((product, i) => {
            const itemClassNames = classNames(
                getProductListItemClassNames(
                    i,
                    props.skipProductsAmountOnMobile ?? 0,
                    props.skipProductsAmountOnTablet ?? 0,
                    props.skipProductsAmountOnDesktop ?? 0,
                    maxOnMobile,
                    maxOnTablet,
                    maxOnDesktop
                ),
                props.withPlaceholders && 'ProductList__Item--WithPlaceholder'
            );

            return (
                <li className={itemClassNames} key={product.code}>
                    <ProductCard
                        profile={profile}
                        cartItemUrl={cartItemUrl}
                        validateItemUrl={validateItemUrl}
                        model={product}
                        variant={getProductCardVariant()}
                        rxUrl={rxUrl}
                        showFavouritesButton={false}
                        epiFindClickTrackingUrl={epiFindClickTrackingUrl}
                        experimentProductClickUrl={experimentProductClickUrl}
                        isLazyLoading
                        hidePricePerUnit
                        hidePowerReviews={hideReviewSnippets}
                        disableQuantityPicker={disableProductQuantityPicker}
                    />
                </li>
            );
        });
    };

    const renderPlaceholders = () => {
        const { maxProductsAmountOnMobile, maxProductsAmountOnTablet, maxProductsAmountOnDesktop } = props.model;
        const placeholderVariant = getProductCardVariant();
        const placeholders = [];

        for (let i = 0; i < Math.max(maxProductsAmountOnMobile, maxProductsAmountOnTablet, maxProductsAmountOnDesktop); i++) {
            placeholders.push(
                <li
                    key={i}
                    className={getProductListItemClassNames(i, 0, 0, 0, maxProductsAmountOnMobile, maxProductsAmountOnTablet, maxProductsAmountOnDesktop)}
                >
                    <ProductCardPlaceholder variant={placeholderVariant} />
                </li>
            );
        }

        return placeholders;
    };

    const getProductListItemClassNames = (
        index: number,
        skipOnMobile: number,
        skipOnTablet: number,
        skipOnDesktop: number,
        maxOnMobile: number,
        maxOnTablet: number,
        maxOnDesktop: number
    ) => {
        return classNames(
            'ProductList__Item',
            (index < skipOnMobile || index >= maxOnMobile + skipOnMobile) && 'ProductList__Item--HiddenOnMobile',
            (index < skipOnTablet || index >= maxOnTablet + skipOnTablet) && 'ProductList__Item--HiddenOnTablet',
            (index < skipOnDesktop || index >= maxOnDesktop + skipOnDesktop) && 'ProductList__Item--HiddenOnDesktop'
        );
    };

    return (
        <UserContextProvider>
            <div className={classNames('ProductList', getVariantModifier(), BlockSectionClassResolver.Resolve(model.blockSectionMargin))}>
                <div className="ProductList__Inner">
                    {props.variant === ProductListVariant.HorizontalSimple ? (
                        <h4 className="ProductList__Heading ProductList__SmallHeading">{heading}</h4>
                    ) : props.variant === ProductListVariant.CartHorizontal ? (
                        <h2>{heading}</h2>
                    ) : (
                        <h2 className="ProductList__Heading BlockSection__Heading">{heading}</h2>
                    )}
                    <ul
                        className={classNames(
                            'ProductList__Items',
                            productListUtils.ShouldBeCentered() && variant != ProductListVariant.CartHorizontal && 'ProductList__Items--Centered'
                        )}
                    >
                        {shouldRenderSwiper() ? (
                            <div className="ProductList__Swiper">
                                <TouchSwiper onlyOnMobile>{renderProductsWithAdditionalBlocks()}</TouchSwiper>
                            </div>
                        ) : (
                            renderProductsWithAdditionalBlocks()
                        )}
                    </ul>
                </div>
            </div>
        </UserContextProvider>
    );
};
